// Pantalla de resumen post-sesión. Refresh-safe: si el route state está
// (happy path desde SesionCamara), lo usa directo. Si el state se perdió
// (refresh, deep-link), fetchea el resumen desde Supabase con el sessionId
// de la URL (ver useResumenSesion). Si ambos fallan → FallbackSinDatos.

import { useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import { Check, AlertCircle, Sparkles, Timer, TrendingUp } from "lucide-react";

import { useResumenSesion } from "@/lib/supabase/queries/useResumenSesion";
import { useRegistrarDolor } from "@/lib/supabase/queries/useRegistrarDolor";
import {
  EscalaDolor,
  labelForLevel,
} from "@/app/components/sessions/EscalaDolor";

/** Shape del resumen que SesionCamara pasa por route state al navegar a
 *  /sesion-completada. Centralizado acá porque la página es la dueña del
 *  contrato — SesionCamara solo lo construye para pasárselo. */
export interface ResumenSesion {
  ejercicioNombre: string;
  /** Tiempo total desde que arrancó la primera serie (post-countdown) hasta
   *  que se cerró la última. En segundos. */
  durationSec: number;
  /** Reps con valid===true sumadas en todas las series. */
  repsValidos: number;
  /** Total de reps objetivo de la sesión (repsObjetivo × seriesObjetivo). */
  repsObjetivo: number;
  /** 0..1, null si no hubo reps válidos (no hay data para promediar). */
  qualityPromedio: number | null;
  /** Violación más frecuente con su copy descriptivo y % de reps afectados.
   *  null si no hubo violaciones (caso ideal: ejecución limpia). */
  topAMejorar: {
    summaryLabel: string;
    frecuenciaPct: number;
  } | null;
  /** Nivel de dolor 1..10 reportado por el paciente, null si todavía no
   *  confirmó. SesionCamara siempre pasa null (la reporta el paciente en
   *  esta pantalla post-finalizar); el fetch refresh-safe trae el valor
   *  de DB si ya se registró. */
  dolorReportado: number | null;
}

function isResumenSesion(value: unknown): value is ResumenSesion {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.ejercicioNombre === "string" &&
    typeof v.durationSec === "number" &&
    typeof v.repsValidos === "number" &&
    typeof v.repsObjetivo === "number" &&
    (v.qualityPromedio === null || typeof v.qualityPromedio === "number") &&
    (v.topAMejorar === null || typeof v.topAMejorar === "object") &&
    (v.dolorReportado === null || typeof v.dolorReportado === "number")
  );
}

function formatDuration(sec: number): string {
  const mm = Math.floor(sec / 60);
  const ss = Math.floor(sec % 60);
  return `${mm}:${ss.toString().padStart(2, "0")}`;
}

export function SesionCompletada() {
  const { state } = useLocation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const stateResumen = isResumenSesion(state) ? state : null;

  // Fast-path: si el state está (navegación happy desde SesionCamara), no
  // hace falta fetch. Si no hay state válido, fetcheamos desde Supabase
  // usando el sessionId de la URL — refresh-safe.
  const fetched = useResumenSesion(sessionId, !stateResumen);

  if (stateResumen) {
    return <ResumenContent resumen={stateResumen} sessionId={sessionId} />;
  }
  if (fetched.loading) return <ResumenSkeleton />;
  if (fetched.data) {
    return <ResumenContent resumen={fetched.data} sessionId={sessionId} />;
  }
  return <FallbackSinDatos />;
}

/** Skeleton del resumen mientras el fetch desde Supabase está en curso.
 *  Mantiene el frame visual (ícono Sparkles + título + cards) con placeholders
 *  pulsantes en los stats — evita pantalla blanca o spinner full-screen
 *  cuando el state se perdió y estamos rehidratando. */
function ResumenSkeleton() {
  return (
    <div className="min-h-screen bg-background flex flex-col p-6 transition-colors duration-200">
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="relative mb-8">
          <div className="absolute inset-0 bg-success/20 rounded-full blur-3xl"></div>
          <div className="relative w-32 h-32 bg-success rounded-full flex items-center justify-center shadow-[0_8px_32px_rgba(132,242,180,0.3)]">
            <Check className="w-16 h-16 text-success-foreground" strokeWidth={3} />
          </div>
        </div>

        <h1 className="text-3xl text-center mb-2">¡Sesión completada!</h1>
        <div className="h-4 w-40 bg-muted rounded animate-pulse mb-8" />

        <div className="w-full max-w-sm space-y-3 mb-8">
          {[Timer, Check, TrendingUp].map((Icon, i) => (
            <div
              key={i}
              className="bg-card rounded-2xl p-4 border border-border"
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-12 h-12 bg-muted rounded-xl">
                  <Icon className="w-6 h-6 text-muted-foreground/50" strokeWidth={1.5} />
                </div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-24 bg-muted rounded animate-pulse" />
                  <div className="h-5 w-16 bg-muted rounded animate-pulse" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ResumenContent({
  resumen,
  sessionId,
}: {
  resumen: ResumenSesion;
  sessionId: string | undefined;
}) {
  const qualityPct =
    resumen.qualityPromedio !== null
      ? Math.round(resumen.qualityPromedio * 100)
      : null;

  // Estado local del dolor — arranca con el valor del resumen (puede venir
  // de DB en refresh-safe path) y se setea local-only al confirmar para
  // no esperar al re-fetch. NO permitir re-editar una vez confirmado en
  // MVP — read-only display si dolor != null.
  const [dolorLocal, setDolorLocal] = useState<number | null>(
    resumen.dolorReportado,
  );
  const { mutate: registrarDolor, loading: dolorSubmitting, error: dolorError } =
    useRegistrarDolor();

  async function handleConfirmarDolor(valor: number) {
    if (!sessionId) return;
    await registrarDolor({ sesion_id: sessionId, dolor: valor });
    // Si la mutation seteó success internamente, el state local refleja
    // el valor inmediatamente sin esperar re-fetch. Si falló, dolorError
    // tiene el mensaje y dolorLocal queda null — el componente EscalaDolor
    // sigue interactivo para reintentar.
    if (!dolorError) setDolorLocal(valor);
  }

  return (
    <div className="min-h-screen bg-background flex flex-col p-6 lg:p-10 transition-colors duration-200">
      {/* Contenido principal.
          Mobile: flex-col centered (stack idéntico al pre-desktop).
          Desktop lg: grid 2×2 — IZQ = título+ejercicio+cards+Para mejorar,
          DER = ícono grande+dolor. DOM en orden natural mobile; posicionamiento
          desktop via lg:col-start/row-start (sin reorder mobile). */}
      <div className="flex-1 flex flex-col items-center justify-center lg:grid lg:grid-cols-2 lg:gap-x-10 lg:gap-y-6 lg:items-start lg:justify-center lg:max-w-5xl lg:mx-auto lg:w-full">
        {/* 1. Ícono grande — mobile top center; desktop col-2 row-1 */}
        <div className="relative mb-8 lg:mb-0 lg:col-start-2 lg:row-start-1 lg:justify-self-center">
          <div className="absolute inset-0 bg-success/20 rounded-full blur-3xl"></div>
          <div className="relative w-32 h-32 bg-success rounded-full flex items-center justify-center shadow-[0_8px_32px_rgba(132,242,180,0.3)]">
            <Check className="w-16 h-16 text-success-foreground" strokeWidth={3} />
          </div>
        </div>

        {/* 2. Título + subtítulo — mobile debajo del ícono; desktop col-1 row-1 */}
        <div className="flex flex-col items-center lg:items-start lg:col-start-1 lg:row-start-1 mb-8 lg:mb-0">
          <h1 className="text-3xl text-center lg:text-left lg:text-4xl mb-2">
            ¡Sesión completada!
          </h1>
          <p className="text-muted-foreground text-center lg:text-left max-w-xs lg:max-w-none">
            {resumen.ejercicioNombre}
          </p>
        </div>

        {/* 3. MetricCards + TopAMejorar — mobile debajo del título; desktop col-1 row-2 */}
        <div className="w-full max-w-sm lg:max-w-none space-y-3 mb-8 lg:mb-0 lg:col-start-1 lg:row-start-2">
          <MetricCard
            icon={<Timer className="w-6 h-6 text-primary" strokeWidth={1.5} />}
            tint="primary"
            label="Duración"
            value={formatDuration(resumen.durationSec)}
          />

          <MetricCard
            icon={<Check className="w-6 h-6 text-success" strokeWidth={1.5} />}
            tint="success"
            label="Reps válidos"
            value={`${resumen.repsValidos} de ${resumen.repsObjetivo}`}
          />

          <MetricCard
            icon={
              <TrendingUp className="w-6 h-6 text-accent" strokeWidth={1.5} />
            }
            tint="accent"
            label="Calidad promedio"
            value={qualityPct !== null ? `${qualityPct}%` : "—"}
          />

          <TopAMejorarCard topAMejorar={resumen.topAMejorar} />
        </div>

        {/* 4. Bloque dolor — mobile 4to; desktop col-2 row-2. Interactivo si
            no hay valor reportado, read-only si ya se confirmó. */}
        <div className="w-full max-w-sm lg:max-w-none mb-8 lg:mb-0 lg:col-start-2 lg:row-start-2">
          {dolorLocal === null ? (
            <div className="bg-card rounded-2xl border border-border overflow-hidden">
              <EscalaDolor
                onConfirmar={handleConfirmarDolor}
                submitting={dolorSubmitting}
                submitError={
                  dolorError
                    ? "No pudimos guardar tu respuesta. Volvé a intentar."
                    : null
                }
              />
            </div>
          ) : (
            <DolorReadOnly valor={dolorLocal} />
          )}
        </div>
      </div>

      {/* Botones — mobile stack full-width; desktop side-by-side centrado
          en el mismo max-w del contenido. */}
      <div className="space-y-3 lg:space-y-0 lg:flex lg:gap-3 lg:max-w-5xl lg:mx-auto lg:w-full lg:mt-8">
        <Link
          to="/progreso"
          className="flex items-center justify-center gap-2 w-full border-2 border-primary text-primary py-4 rounded-full text-center font-semibold transition-all duration-200 ease-out hover:bg-primary/5"
        >
          Ver mi progreso
        </Link>
        <Link
          to="/paciente-home"
          className="block w-full bg-primary text-primary-foreground py-4 rounded-full text-center font-semibold shadow-[0_2px_4px_rgba(0,59,74,0.2)] transition-all duration-200 ease-out hover:shadow-[0_4px_8px_rgba(0,59,74,0.3)] hover:scale-[1.02]"
        >
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}

/** Display read-only del dolor reportado. Aparece después del Confirmar
 *  o al refrescar la pantalla cuando el valor ya está en DB. MVP no
 *  permite re-editar (ver pain-indicator-mvp.md). */
function DolorReadOnly({ valor }: { valor: number }) {
  return (
    <div className="bg-card rounded-2xl p-4 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
      <div className="flex items-center gap-4">
        <div className="flex items-center justify-center w-12 h-12 bg-accent/20 rounded-xl">
          <AlertCircle className="w-6 h-6 text-accent" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-muted-foreground">Dolor reportado</p>
          <p className="text-xl font-[800] tabular-nums">
            {valor}/10{" "}
            <span className="text-base font-semibold text-muted-foreground capitalize">
              · {labelForLevel(valor)}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  icon,
  tint,
  label,
  value,
}: {
  icon: React.ReactNode;
  tint: "primary" | "success" | "accent";
  label: string;
  value: string;
}) {
  const tintClass = {
    primary: "bg-primary/20",
    success: "bg-success/20",
    accent: "bg-accent/20",
  }[tint];

  return (
    <div className="bg-card rounded-2xl p-4 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
      <div className="flex items-center gap-4">
        <div
          className={`flex items-center justify-center w-12 h-12 ${tintClass} rounded-xl`}
        >
          {icon}
        </div>
        <div className="flex-1">
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-xl font-[800] tabular-nums">{value}</p>
        </div>
      </div>
    </div>
  );
}

function TopAMejorarCard({
  topAMejorar,
}: {
  topAMejorar: ResumenSesion["topAMejorar"];
}) {
  if (topAMejorar === null) {
    return (
      <div className="bg-gradient-to-br from-success/10 to-primary/10 rounded-3xl p-6 w-full border border-success/20">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-5 h-5 text-success" strokeWidth={1.5} />
          <h3 className="font-semibold">Sin observaciones</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Ejecutaste la sesión sin desviaciones notables. Bien ahí.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-accent/10 to-primary/10 rounded-3xl p-6 w-full border border-accent/20">
      <div className="flex items-center gap-3 mb-2">
        <AlertCircle className="w-5 h-5 text-accent" strokeWidth={1.5} />
        <h3 className="font-semibold">Para mejorar</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        {topAMejorar.summaryLabel}{" "}
        <span className="font-semibold text-accent">
          ({topAMejorar.frecuenciaPct}% de los reps)
        </span>
      </p>
    </div>
  );
}

function FallbackSinDatos() {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="max-w-sm w-full text-center">
        <h1 className="text-2xl font-semibold mb-2">Sesión completada</h1>
        <p className="text-muted-foreground mb-8">
          No hay datos para mostrar de esta sesión. Volvé al inicio para ver
          tu rutina.
        </p>
        <Link
          to="/paciente-home"
          className="block w-full bg-primary text-primary-foreground py-4 rounded-full text-center font-semibold shadow-[0_2px_4px_rgba(0,59,74,0.2)] transition-all duration-200 ease-out hover:shadow-[0_4px_8px_rgba(0,59,74,0.3)] hover:scale-[1.02]"
        >
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}
